import { useEffect, useMemo, useState } from "react";
import Header from "../layout/header";
import { _get } from "../libraries/fetch";
import { getAllBranchesURL, getBranchURL } from "../libraries/apiUrl";
import OnboardingStatus from "../features/branch/components/OnboardingStatus";

// Keep onboarding branch list aligned with Clients page visibility.
const IGNORE_LIST = new Set([
  "64bf9fe56e1eb79068263fa6",
  "64e5e8f81ca9289ef69a8f3e",
  "64e72bc41ca9289ef6a2ee23",
  "64e84af91ca9289ef6aa8141",
  "64f1c5fc4a74d6538da18e86",
  "651250a01a6bd490a80bb134",
  "665adefe634ebea0973fdb28",
  "6475b8f9ca1bb21ff2fa6913",
  "65d4408ad4d1db5f96081755",
  "65dc317ca2795b43341426f7",
  "6690c8339f9b8cae831b242a",
  "6902f3f64cf88446d8589c25",
  "690300394cf88446d858ef08",
  "690301be4cf88446d858f7f7",
  "6903037c4cf88446d8590436",
  "690304b94cf88446d8590c57",
  "683e8b9a4cf88446d8088b45",
  "67b2faae17701c2c24df4dae",
  "683e78654cf88446d8082575",
  "67879f84165a406d9a846583",
  "66ba093c535ed317419a63a2",
  "68735b604cf88446d8bf2871",
  "68de1d204cf88446d87c5a26",
  "675aae0d12e3fdda6677d2df",
  "6642eab31678eb98b616af02",
  "678f384699156d3a1d3aa6b7",
  "685e43704cf88446d872c023"
]);

function extractBranchDetails(response) {
  if (!response || typeof response !== "object") {
    return null;
  }

  if (response._id || response.id || response.name || response.branchName) {
    return response;
  }

  return response.response || response.data || response.result || null;
}

function OnboardingStatusPage() {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedBranchDetail, setSelectedBranchDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    _get(getAllBranchesURL(), { useCache: false })
      .then((data) => {
        let branchList = [];
        if (Array.isArray(data)) {
          branchList = data;
        } else if (Array.isArray(data?.response)) {
          branchList = data.response;
        } else if (Array.isArray(data?.data)) {
          branchList = data.data;
        }

        const clean = branchList.filter(
          (branch) => branch && branch._id && branch.name && !IGNORE_LIST.has(branch._id)
        );
        setBranches(clean);

        if (clean.length > 0) {
          setSelectedBranchId(clean[0]._id);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedBranchId) {
      setSelectedBranchDetail(null);
      return;
    }

    _get(getBranchURL(selectedBranchId), { useCache: false })
      .then((data) => {
        setSelectedBranchDetail(extractBranchDetails(data));
      })
      .catch(() => {
        setSelectedBranchDetail(null);
      });
  }, [selectedBranchId]);

  const selectedBranch = useMemo(() => {
    return branches.find((branch) => branch._id === selectedBranchId) || null;
  }, [branches, selectedBranchId]);

  const activeBranch = useMemo(() => {
    if (!selectedBranch) {
      return null;
    }

    return {
      ...selectedBranch,
      ...(selectedBranchDetail || {})
    };
  }, [selectedBranch, selectedBranchDetail]);

  return (
    <div className="main">
      <Header />
      <div
        style={{
          padding: "clamp(16px, 3vw, 20px) clamp(12px, 3vw, 20px)",
          maxWidth: "100%",
          boxSizing: "border-box",
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #1e2127 0%, #2a2d35 100%)"
        }}
      >
        <div
          style={{
            backgroundColor: "#20232b",
            border: "1px solid #3a3f4b",
            borderRadius: "10px",
            padding: "16px",
            marginBottom: "16px",
            maxWidth: "min(520px, 100%)",
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          <div
            style={{
              marginBottom: "10px",
              color: "#fff",
              fontWeight: 700,
              fontSize: "16px"
            }}
          >
            Select Client
          </div>

          {isLoading && <div style={{ color: "#9ca3af" }}>Loading clients...</div>}

          {!isLoading && branches.length === 0 && (
            <div style={{ color: "#9ca3af" }}>No clients found.</div>
          )}

          {!isLoading && branches.length > 0 && (
            <select
              value={selectedBranchId}
              onChange={(event) => setSelectedBranchId(event.target.value)}
              style={{
                width: "100%",
                backgroundColor: "#12151d",
                border: "1px solid #3a3f4b",
                borderRadius: "8px",
                color: "#fff",
                padding: "10px 12px",
                fontSize: "14px",
                outline: "none"
              }}
            >
              {branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <section style={{ minWidth: 0 }}>
          {activeBranch ? (
            <OnboardingStatus
              branchId={activeBranch._id}
              branchName={activeBranch.name}
              companyId={activeBranch.company || activeBranch.companyId}
              branch={activeBranch}
            />
          ) : (
            <div
              style={{
                backgroundColor: "#20232b",
                border: "1px solid #3a3f4b",
                borderRadius: "10px",
                padding: "20px",
                color: "#9ca3af"
              }}
            >
              Select a branch from the left side to manage onboarding status.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default OnboardingStatusPage;
